import React, { useState, useEffect } from "react";
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Stack,
    Alert,
    CircularProgress
} from "@mui/material";
import {
    People as PeopleIcon,
    Business as BusinessIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext";
import { usePermissions } from "../../../utils/permissions";

/**
 * Dashboard principal para administradores de negocio
 */
const BusinessDashboard = () => {
    const { user } = useAuth();
    const permissions = usePermissions(user);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        pendingKYC: 0,
        activeOpportunities: 0,
        totalInvestments: 0,
        pendingOffboarding: 0,
        activeAgents: 0
    });

    useEffect(() => {
        loadDashboardStats();
    }, []);

    const loadDashboardStats = async () => {
        try {
            setLoading(true);
            // TODO: Implementar carga de estadísticas reales
            // Simular datos para demostración
            setTimeout(() => {
                setStats({
                    totalUsers: 1247,
                    pendingKYC: 23,
                    activeOpportunities: 8,
                    totalInvestments: 156,
                    pendingOffboarding: 5,
                    activeAgents: 45
                });
                setLoading(false);
            }, 1000);
        } catch (error) {
            // console.error("Error loading dashboard stats:", error);
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon, color, subtitle }) => (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                        sx={{
                            backgroundColor: `${color}.light`,
                            borderRadius: "50%",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        {icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                            {value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    const QuickActionCard = ({ title, description, icon, action, color = "primary" }) => (
        <Card sx={{ height: "100%", cursor: "pointer" }} onClick={action}>
            <CardContent>
                <Stack spacing={2}>
                    <Box
                        sx={{
                            backgroundColor: `${color}.light`,
                            borderRadius: "50%",
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header */}
                <Box>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
                        Dashboard de Negocio
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Bienvenido, {user?.displayName || user?.email}. 
                        Nivel de acceso: {permissions.getUserAccessLevel()}
                    </Typography>
                </Box>

                {/* Alertas */}
                {stats.pendingKYC > 0 && (
                    <Alert severity="warning" icon={<WarningIcon />}>
                        <Typography variant="body1">
                            Tienes {stats.pendingKYC} solicitudes de KYC pendientes de revisión
                        </Typography>
                    </Alert>
                )}

                {stats.pendingOffboarding > 0 && (
                    <Alert severity="info" icon={<AssignmentIcon />}>
                        <Typography variant="body1">
                            {stats.pendingOffboarding} usuarios en proceso de offboarding
                        </Typography>
                    </Alert>
                )}

                {/* Estadísticas principales */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Usuarios Totales"
                            value={stats.totalUsers.toLocaleString()}
                            icon={<PeopleIcon sx={{ color: "primary.main" }} />}
                            color="primary"
                            subtitle="Usuarios registrados"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="KYC Pendientes"
                            value={stats.pendingKYC}
                            icon={<WarningIcon sx={{ color: "warning.main" }} />}
                            color="warning"
                            subtitle="Requieren revisión"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Oportunidades Activas"
                            value={stats.activeOpportunities}
                            icon={<BusinessIcon sx={{ color: "success.main" }} />}
                            color="success"
                            subtitle="Disponibles para inversión"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Inversiones Totales"
                            value={stats.totalInvestments.toLocaleString()}
                            icon={<TrendingUpIcon sx={{ color: "info.main" }} />}
                            color="info"
                            subtitle="Inversiones realizadas"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Offboarding Pendiente"
                            value={stats.pendingOffboarding}
                            icon={<AssignmentIcon sx={{ color: "secondary.main" }} />}
                            color="secondary"
                            subtitle="En proceso"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Agentes Activos"
                            value={stats.activeAgents}
                            icon={<PeopleIcon sx={{ color: "success.main" }} />}
                            color="success"
                            subtitle="Agentes operativos"
                        />
                    </Grid>
                </Grid>

                {/* Acciones rápidas */}
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
                        Acciones Rápidas
                    </Typography>
                    <Grid container spacing={3}>
                        {permissions.hasPermission("business:manage_kyc") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Revisar KYC"
                                    description={`${stats.pendingKYC} solicitudes pendientes`}
                                    icon={<CheckCircleIcon />}
                                    color="warning"
                                    action={() => window.location.href = "/admin/business/kyc-pending"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("business:manage_users") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Gestionar Usuarios"
                                    description="Administrar usuarios del sistema"
                                    icon={<PeopleIcon />}
                                    color="primary"
                                    action={() => window.location.href = "/admin/business/users"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("business:manage_agents") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Gestionar Agentes"
                                    description={`${stats.activeAgents} agentes activos`}
                                    icon={<BusinessIcon />}
                                    color="success"
                                    action={() => window.location.href = "/admin/business/agents"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("business:manage_opportunities") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Oportunidades"
                                    description={`${stats.activeOpportunities} oportunidades activas`}
                                    icon={<TrendingUpIcon />}
                                    color="info"
                                    action={() => window.location.href = "/admin/business/opportunities"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("business:manage_offboarding") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Offboarding"
                                    description={`${stats.pendingOffboarding} procesos pendientes`}
                                    icon={<AssignmentIcon />}
                                    color="secondary"
                                    action={() => window.location.href = "/admin/business/offboarding"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("audit:view_reports") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Reportes"
                                    description="Ver reportes de compliance"
                                    icon={<TrendingUpIcon />}
                                    color="primary"
                                    action={() => window.location.href = "/admin/business/compliance-reports"}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Información de permisos */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Tus Permisos
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {permissions.getUserPermissions().map((permission) => (
                                <Chip
                                    key={permission}
                                    label={permission}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    );
};

export default BusinessDashboard; 